import React, { useState, ChangeEvent, FormEvent, useRef, useEffect, useContext } from 'react';
import { DocFormProps, Field } from '../types';
import OwnerSearch from '../OwnerSearch';
import Consumer from '../Consumer';
import { remove, set } from 'lodash';
import { useOutsideAlerter } from '../useOutsideAlerter';


const DynamicForm: React.FC<DocFormProps> = ({ docId, docName, docDescription, docForm, status, setDocForm, handleSubmit, owner, setOwner, setOwnerNeedsUpdate, consumers, addConsumer, removeConsumer, setStatus, setNeedsUpdate, setDocDescription, setDocName, loading }) => {
  const [isDocFormCollapsed, setIsDocFormCollapsed] = useState<boolean>(false);
  const [isSchemaCollapsed, setIsSchemaCollapsed] = useState<boolean>(true);
  const [documentName, setDocumentName] = useState<string>(docName || 'Untitled');
  const [documentDescription, setDocumentDescription] = useState<string>(docDescription || "Enter a description");
  const [editName, setEditName] = useState<boolean>(false);
  const [editDescription, setEditDescription] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<{ [key: string]: string }>(
    docForm && docForm.fields
      ? docForm.fields.reduce((values: { [key: string]: string }, field: Field) => {
        values[field.name] = field.value || '';
        return values;
      }, {})
      : {}
  );

  const nameRef = useRef(null);
  const descriptionRef = useRef(null);

  useOutsideAlerter(nameRef, () => {
    setDocName(documentName); 
    setEditName(false);
    if (documentName !== docName && docForm) {
      setNeedsUpdate(true);
    }
  });

  useOutsideAlerter(descriptionRef, () => {
    setDocDescription(documentDescription);
    setEditDescription(false)
    if (documentDescription !== docDescription && docForm) {
      setNeedsUpdate(true);
    }
  });

  const toggleDocForm = () => {
    setIsDocFormCollapsed(!isDocFormCollapsed);
  };

  const toggleSchema = () => {
    setIsSchemaCollapsed(!isSchemaCollapsed);
  };

  const [statusValue, setStatusValue] = useState(status || "draft");

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => {
    const updatedFormValues = { ...formValues, [fieldName]: event.target.value };
    setFormValues(updatedFormValues);
    setDocForm({ ...docForm, fields: docForm.fields.map((field) => ({ ...field, value: updatedFormValues[field.name] })) });
  };

  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // update docForm
    if (docForm) {
      setDocForm({ ...docForm, fields: docForm.fields.map((field) => ({ ...field, value: formValues[field.name] })) });
    }
  };

  const StatusPopup: React.FC<{ status: string, setStatusValue: any }> = ({ status, setStatusValue }) => {
    const [showPopup, setShowPopup] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);

    const handleStatusUpdate = () => {
      setStatusValue(status === 'draft' ? 'live' : 'draft');
      setStatus(status === 'draft' ? 'live' : 'draft');
      setNeedsUpdate(true);
      console.log('Status updated');
      setShowPopup(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowPopup(false);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <div className="justify-start">
        {status && (
          <span
            className={`px-2 py-1 text-sm font-medium rounded-md uppercase cursor-pointer ${status === 'draft' ? 'bg-yellow-400 text-white' : 'bg-green-500 text-white'
              }`}
            onClick={() => setShowPopup(true)}
          >
            {status}
          </span>
        )}
        {showPopup && (
          <>
            <div className="grid place-content-center fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
              <div ref={popupRef} className="bg-blue-50 p-4 rounded-md shadow-lg">
                <p className="mb-2">
                  {status === 'draft' ? 'Clicking "Update Status" will publish the document.' : 'Clicking "Update Status" will mark the document as a draft.'}
                </p>
                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md"
                  onClick={handleStatusUpdate}
                >
                  Update Status
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };



  return (
    formValues ? (
      <form onSubmit={handleSave} className="pb-6">
        <div>
          <div className="flex flex-row justify-between">
            <div className="hover:cursor-pointer hover:shadow-sm rounded-lg grow mr-3" onClick={() => setEditName(true)} ref={nameRef}>
              {editName ? (
                      <div className="">
                      <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                        <input
                          type="text"
                          name="name"
                          id="name"
                          autoComplete="off"
                          className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-gray-900 placeholder:text-gray-400 rounded-md focus:border-blue-300 focus:ring focus:ring-blue-200 sm:text-3xl focus:ring-opacity-50 sm:leading-6"
                          placeholder=""
                          value={documentName || ''}
                          onChange={(e) => setDocumentName(e.target.value)}
                        />
                      </div>
                    </div>
              ) : (
                <h2 className="text-3xl font-semibold leading-7 text-gray-900 py-1.5 ">{docName != "" ? docName : documentName}</h2>
              )}
            </div>
            <StatusPopup status={statusValue} setStatusValue={setStatusValue} />
          </div>
        </div>
        {/* <h2 className="text-md font-semibold leading-7 text-blue-900 mt-1">{`ID: ${docId}`}</h2> */}
        <div className="hover:cursor-pointer hover:shadow-sm rounded-lg" onClick={() => setEditDescription(true)} ref={descriptionRef}>
          {editDescription ? (
            <div className="mt-3">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                <input
                  type="text"
                  name="description"
                  id="description"
                  autoComplete="off"
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-gray-900 placeholder:text-gray-400 rounded-md focus:border-blue-300 focus:ring focus:ring-blue-200 sm:text-md focus:ring-opacity-50 sm:leading-6"
                  placeholder=""
                  value={documentDescription || ''}
                  onChange={(e) => setDocumentDescription(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <p className="mt-3 text-md leading-6 text-gray-600 py-1.5">{docDescription != "" ? docDescription : documentDescription}</p>
          )}
        </div>
        {docId && (
          <div>
        <div className="flex items-center cursor-pointer flex flex-row mt-5" onClick={() => toggleDocForm()}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="gray" className={`w-5 h-5 mr-1 transition-transform ${isDocFormCollapsed ? '-rotate-90' : ''}`}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
          <span className="text-lg font-medium text-gray-600">General</span>
        </div>
        {!isDocFormCollapsed && (
          <div className="mt-4 flex flex-col">
            <div className="flex flex-row">

                <div className="flex flex-col">
                <OwnerSearch setUserCallback={setOwner} owner={owner} setOwnerNeedsUpdate={setOwnerNeedsUpdate} />
                <Consumer consumers={consumers} addConsumer={addConsumer} removeConsumer={removeConsumer} />
                </div>

            </div>
            {/* {docForm && docForm.fields && docForm.fields.map((field, index) => (
              <div key={field.name} className={`mt-${index === 0 ? '4' : '10'} gap-x-6 gap-y-8`}>
                {field.type === 'text' && (
                  <div className="">
                    <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-gray-900">
                      {field.label}
                    </label>

                    <div className="mt-3">
                      <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                        <input
                          type="text"
                          name={field.name}
                          id={field.name}
                          autoComplete={field.name}
                          className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-gray-900 placeholder:text-gray-400 rounded-md focus:border-blue-300 focus:ring focus:ring-blue-200 sm:text-sm focus:ring-opacity-50 sm:leading-6"
                          placeholder=""
                          value={field.value || ''}
                          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange(e, field.name)}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {field.type === 'textarea' && (
                  <div className="">
                    <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-gray-900">
                      {field.label}
                    </label>
                    <div className="mt-3">
                      <textarea
                        id={field.name}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 sm:text-sm focus:ring-opacity-50"
                        rows={4}
                        value={field.value || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange(e, field.name)}
                      ></textarea>
                    </div>
                  </div>
                )}
              </div>
            ))} */}
          </div>
        )}
        </div>
        )}

      </form>
    ) : (
      <div className="flex justify-center items-center h-96">
      </div>
    )

  );
};

export default DynamicForm;
